import { useEffect, useRef } from 'react';

import { useServerEvent } from './useServerEvent';

export interface SyncEventPayload {
  accountId: string;
  userId: string;
  syncType: string[];
  skipped?: boolean;
  reason?: string;
  success?: boolean;
  at: number;
}

/**
 * Gộp các event dồn trong một cửa sổ ngắn thành MỘT lần gọi `onEvent`.
 *
 * Worker đồng bộ nhiều account liên tiếp sẽ bắn nhiều `sync-completed` gần như
 * cùng lúc; gọi thẳng mỗi cái là bấy nhiêu lượt `reload()` → burst request +
 * render thừa. Cửa sổ nhỏ (~150ms) gom cả cụm còn một lần, độ trễ vẫn gần như 0.
 */
const COALESCE_MS = 150;

/**
 * Nghe SSE `event: sync-completed` (server lọc theo user) qua connection dùng
 * chung {@link useServerEvent}, gom cụm rồi gọi `onEvent`.
 *
 * @param onEvent callback khi nhận event `sync-completed`.
 * @param enabled false thì không nghe (vd chưa đăng nhập).
 */
export function useSyncEvents(onEvent: (payload: SyncEventPayload) => void, enabled = true): void {
  const cbRef = useRef(onEvent);
  cbRef.current = onEvent;

  const pendingRef = useRef<SyncEventPayload | null>(null);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useServerEvent(
    'sync-completed',
    (data) => {
      let payload: SyncEventPayload;
      try {
        payload = JSON.parse(data) as SyncEventPayload;
      } catch {
        return; // Bỏ qua frame hỏng định dạng.
      }
      // Gom cụm: lưu payload mới nhất, hẹn MỘT lần flush trong cửa sổ ngắn.
      pendingRef.current = payload;
      if (flushTimerRef.current) return;
      flushTimerRef.current = setTimeout(() => {
        flushTimerRef.current = null;
        const next = pendingRef.current;
        pendingRef.current = null;
        if (next) cbRef.current(next);
      }, COALESCE_MS);
    },
    enabled,
  );

  // Hủy timer gom cụm khi unmount HOẶC khi enabled→false (tắt nghe): không để
  // callback fire gọi `onEvent` sau khi đã ngừng nghe SSE.
  useEffect(() => {
    return () => {
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      pendingRef.current = null;
    };
  }, [enabled]);
}
